import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Checkbox, Collapse, Empty, Form, Input, Popconfirm, Select, Space, Typography, message } from "antd";
import { driverMatchesSearch, sortedDriverBuses } from "../utils/driverSearch";
import api from "../api/axios";
const { Title, Text } = Typography;
const UPDATE_REQUIRED = "Das Backend unterstützt die aktuelle Fahrerverwaltung noch nicht. Backend neu bauen, JAR ersetzen und Dienst neu starten.";
const errorText = error => error.response?.status === 405 ? UPDATE_REQUIRED : error.response?.data?.message || error.message || "Die Änderung konnte nicht gespeichert werden.";

export default function DriversPage() {
  const [notice, noticeHolder] = message.useMessage();
  const [drivers, setDrivers] = useState([]);
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("ALL");
  const [busFilter, setBusFilter] = useState();
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm();
  const load = async () => {
    setLoading(true); setError("");
    try {
      const capabilities = await api.get("/drivers/capabilities").catch(() => { throw new Error(UPDATE_REQUIRED); });
      if (capabilities.data?.version !== 2) throw new Error(UPDATE_REQUIRED);
      // Busliste bleibt unabhängig vom gerade gewählten Ausschreibungsmodus.
      const [accounts, vehicles] = await Promise.all([api.get("/drivers"), api.get("/drivers/buses")]);
      setDrivers(accounts.data); setBuses(vehicles.data);
    } catch (failure) { setError(errorText(failure)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const options = sortedDriverBuses(buses).map(bus => ({ value: bus.id, label: bus.name || `Bus ${bus.id}` }));
  const create = async values => {
    setBusy(true);
    try {
      await api.post("/drivers", { ...values, busIds: values.busIds || [] });
      form.resetFields(); notice.success("Fahrerzugang angelegt. Zugangsdaten bitte persönlich übergeben."); await load();
    } catch (failure) { notice.error(errorText(failure)); }
    finally { setBusy(false); }
  };
  const save = async driver => {
    setBusy(true);
    try {
      const { data } = await api.put(`/drivers/${driver.id}`, { name: driver.name, email: driver.email, busIds: driver.busIds, active: driver.active });
      if (data.name !== driver.name?.trim() || data.email !== driver.email?.trim().toLowerCase()) throw new Error("Der Server hat die Änderungen nicht übernommen. Bitte Backend-Version prüfen.");
      const accounts = await api.get("/drivers");
      if (!accounts.data.some(item => item.id === data.id && item.name === data.name && item.email === data.email)) throw new Error("Die Änderung ist beim erneuten Laden nicht vorhanden.");
      setDrivers(accounts.data);
      notice.success("Zugang gespeichert.");
    } catch (failure) { notice.error(errorText(failure)); }
    finally { setBusy(false); }
  };
  const remove = async driver => {
    setBusy(true);
    try {
      await api.delete(`/drivers/${driver.id}`);
      setDrivers(current => current.filter(item => item.id !== driver.id));
      notice.success("Fahrerzugang gelöscht.");
    } catch (failure) { notice.error(errorText(failure)); }
    finally { setBusy(false); }
  };
  const edit = (id, patch) => setDrivers(current => current.map(driver => driver.id === id ? { ...driver, ...patch } : driver));
  return <Space direction="vertical" size="large" style={{ width: "100%" }}>
    {noticeHolder}
    <Title level={2} style={{ margin: 0 }}>Fahrerzugänge</Title>
    <Alert type="info" showIcon message="Fahrer sehen ausschließlich die gespeicherten Fahrpläne ihrer zugewiesenen Busse."
      description="Der Zugang umfasst alle Wochentage und beide Fahrtrichtungen dieser Busse aus den gespeicherten Ergebnissen des eigenen Fuhrparks. Änderungen gelten nach dem Speichern. Kosten und Bearbeitung bleiben gesperrt. Zur Anmeldung wird ein Code an die Fahrer-E-Mail gesendet." />
    {error && <Alert type="error" showIcon message="Zugänge konnten nicht geladen werden." description={error} action={<Button onClick={load}>Erneut laden</Button>} />}
    <Card title="Fahrerzugang anlegen" loading={loading}>
      <Form form={form} layout="vertical" onFinish={create}>
        <Form.Item name="name" label="Name des Fahrers" rules={[{ required: true, whitespace: true, max: 200, message: "Fahrername eingeben (maximal 200 Zeichen)." }]}><Input autoComplete="name" /></Form.Item>
        <Form.Item name="email" label="E-Mail-Adresse des Fahrers" rules={[{ required: true, type: "email", message: "Gültige E-Mail eingeben." }]}><Input autoComplete="off" /></Form.Item>
        <Form.Item name="password" label="Startpasswort" rules={[{ required: true, min: 12, message: "Mindestens 12 Zeichen verwenden." }]}><Input.Password autoComplete="new-password" /></Form.Item>
        <Form.Item name="busIds" label="Freigegebene Busse"><Select mode="multiple" showSearch optionFilterProp="label" options={options} placeholder="Busse auswählen" /></Form.Item>
        <Space wrap style={{ marginBottom: 16 }}>
          <Button disabled={busy || loading || Boolean(error)} onClick={() => form.setFieldValue("busIds", buses.map(bus => bus.id))}>Alle Busse zuweisen</Button>
          <Button disabled={busy} onClick={() => form.setFieldValue("busIds", [])}>Alle Busse entfernen</Button>
        </Space>
        <div><Button type="primary" htmlType="submit" loading={busy} disabled={Boolean(error)}>Zugang anlegen</Button></div>
      </Form>
    </Card>
    {!loading && !error && !drivers.length && <Empty description="Noch keine Fahrerzugänge angelegt." />}
    <Space wrap>
      <Input.Search aria-label="Fahrer suchen" placeholder="Fahrer, E-Mail, Fahrgast, Depot oder Fahrzeug suchen" style={{ width: 440, maxWidth: "100%" }} allowClear value={query} onChange={event => setQuery(event.target.value)} />
      <Select aria-label="Zugangsstatus" value={status} onChange={setStatus} options={[{ value: "ALL", label: "Alle Zugänge" }, { value: "ACTIVE", label: "Aktiv" }, { value: "INACTIVE", label: "Gesperrt" }]} />
      <Select aria-label="Nach Bus filtern" placeholder="Alle Busse" allowClear showSearch optionFilterProp="label" options={options} value={busFilter} onChange={setBusFilter} style={{ minWidth: 200 }} />
    </Space>
    <Collapse defaultActiveKey={[]} items={drivers.filter(driver => driverMatchesSearch(driver, buses, query)
      && (status === "ALL" || driver.active === (status === "ACTIVE"))
      && (busFilter === undefined || driver.busIds.includes(busFilter)))
      .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email, "de", { numeric: true }) || a.email.localeCompare(b.email, "de"))
      .map(driver => ({ key: String(driver.id), label: <span style={{ display: "flex", flexWrap: "wrap", gap: "8px 24px", overflowWrap: "anywhere" }}><span>{driver.name || "Name noch ergänzen"}</span><Text type="secondary">{driver.email}</Text></span>, children: <>

      <Space direction="vertical" style={{ width: "100%" }}>
        <Text>Name des Fahrers</Text>
        <Input aria-label={`Name für ${driver.email}`} maxLength={200} disabled={busy} value={driver.name || ""} onChange={event => edit(driver.id, { name: event.target.value })} />
        <Text>E-Mail-Adresse des Fahrers</Text>
        <Input aria-label={`E-Mail für ${driver.name || driver.id}`} type="email" maxLength={254} value={driver.email} disabled={busy} onChange={event => edit(driver.id, { email: event.target.value })} />
        <Text>Freigegebene Busse</Text>
        <Select aria-label={`Buszuordnung für ${driver.email}`} mode="multiple" showSearch optionFilterProp="label" style={{ width: "100%" }} value={driver.busIds}
          options={options} onChange={busIds => edit(driver.id, { busIds })} disabled={busy} />
        <Space wrap>
          <Button disabled={busy || Boolean(error)} onClick={() => edit(driver.id, { busIds: buses.map(bus => bus.id) })}>Alle Busse zuweisen</Button>
          <Button disabled={busy} onClick={() => edit(driver.id, { busIds: [] })}>Alle Busse entfernen</Button>
          <Text type="secondary">Mit „Änderungen speichern“ übernehmen.</Text>
        </Space>
        <Checkbox checked={driver.active} onChange={event => edit(driver.id, { active: event.target.checked })} disabled={busy}>Zugang aktiv</Checkbox>
        <Space wrap>
          <Button type="primary" onClick={() => save(driver)} loading={busy}>Änderungen speichern</Button>
          <Popconfirm title="Fahrerzugang löschen?" description="Der Fahrer kann sich danach nicht mehr anmelden. Die Planung bleibt erhalten." okText="Löschen" cancelText="Abbrechen" onConfirm={() => remove(driver)}>
            <Button danger disabled={busy}>Fahrer löschen</Button>
          </Popconfirm>
        </Space>
      </Space>
    </> }))} />
  </Space>;
}
